import { ChangeEvent, useMemo } from 'react'
import { useAppDispatch } from 'hooks/redux'
import styled from '@emotion/styled'
import Button from 'react-bootstrap/Button'
import ButtonGroup from 'react-bootstrap/ButtonGroup'
import ReactSelect from 'react-select'
import { CircularProgress } from '@mui/material'

import Select from 'components/atoms/Select'
import { formalName, matchesEveryWord } from 'helpers/nameHelper'
import Input from 'components/atoms/Input'
import InputMask from 'components/atoms/InputMask'
import Toast from 'components/atoms/Toast'
import { SearchParams } from './Order'
import { CompanyDto, Offices, Physician, SavedBucketDto } from 'store/types'
import { PayerSearchItem } from 'ts-back-end/src/business/payer/dto/payer.dto'
import { filterPayerOption, formatPayerOptionLabel } from 'components/lab/payer/PayerOptionLabel'
import Dropdown from 'components/atoms/Dropdown'
import { OrderTableHelpers } from 'hooks/useOrdersTable'
import { FollowUpFilterConfig, hasWesperDropshipFilter, OUTSOURCE_VISIBILITY_BUCKETS } from './columnConfig'
import { useUpdateLabUserNotificationBucketsMutation, useUpdateLabUserSavedBucketsMutation } from 'store/services/users'
import { setLabSavedBucketsState, useLabSavedBuckets, } from 'components/lab/state/savedBucketsStore'
import { setSoundAlertBucketsState, useSoundAlertBuckets } from 'components/lab/state/soundAlertBucketsStore'
import { soundNotificationService } from 'services/soundNotification.service'
import { notificationBuckets } from "constants/notificationBuckets";
import { setNotificationBuckets } from 'store/slice/initSlice'

const FlexRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const InputWrapper = styled.div`
  flex: 1;
  input,
  select {
    height: 38px;
  }

  @media (max-width: 991px) {
    flex: none;
  }
`

const orderStateOptions = [
  { key: 'verified', value: 'Only Verified Orders' },
  { key: 'failedVerification', value: 'Only Failed Verification' },
  { key: 'aobVerified', value: 'Only AOB Verified' },
  { key: 'hasUploads', value: 'Only Orders with Uploads' },
]

const authorizationStateOptions = [
  { key: 'STARTED', value: 'Started' },
  { key: 'PENDING', value: 'Not Started' },
]

const billingStateOptions = [
  { key: 'billed', value: 'Billed' },
  { key: 'nonBilled', value: 'Non-Billed' },
  { key: 'review', value: 'Billing Review' },
  { key: 'pending', value: 'Pending to CureMD' },
]

const humanFollowUpOptions = [
  { key: 'all', value: 'All Orders' },
  { key: 'humanOnly', value: 'Human Follow-up Only' },
]

const wesperDropshipOptions = [
  { key: 'hide', value: 'Hide Wesper Dropship' },
  { key: 'show', value: 'Show Wesper Dropship' },
]

const outsourceVisibilityOptions = [
  { key: 'visible', value: 'Visible to Outsource' },
  { key: 'hidden', value: 'Hidden from Outsource' },
]

interface IProps {
  search: SearchParams
  changeSearch: (field: string, value: string | boolean) => void
  clearSearch: () => void
  physicians: Physician[]
  payers?: PayerSearchItem[]
  companies?: CompanyDto
  offices?: Offices
  tableHelpers: OrderTableHelpers
  showExport?: boolean
  physiciansLoading?: boolean
  onCompanySearch?: (search: string) => void
  currentUserId?: number
  currentSavedBucket?: SavedBucketDto | null
  followUpFilterConfig?: FollowUpFilterConfig
}

export const AdvancedSearch = ({
  offices,
  payers,
  companies,
  search,
  changeSearch,
  clearSearch,
  tableHelpers,
  showExport = false,
  physicians,
  physiciansLoading,
  onCompanySearch,
  currentUserId,
  currentSavedBucket,
  followUpFilterConfig,
}: IProps) => {
  const dispatch = useAppDispatch()
  const savedBuckets = useLabSavedBuckets()
  const soundAlertBuckets = useSoundAlertBuckets()
  const [updateLabUserSavedBuckets] = useUpdateLabUserSavedBucketsMutation()
  const [updateLabUserNotificationBuckets] = useUpdateLabUserNotificationBucketsMutation()
  const physicianOptions = useMemo(
    () => physicians?.map((p) => ({ value: p.npi, label: formalName(p.firstName, p.lastName) })),
    [physicians],
  )

  const companiesOptions = useMemo(
    () =>
      (Array.isArray(companies) ? companies : companies ? [companies] : [])?.map((c) => ({
        value: c.id,
        label: c.companyName,
      })),
    [companies],
  )

  const officesOptions = useMemo(
    () =>
      offices
        ?.filter((o) => {
          if (search.companyId) {
            return Number(o.companyId) === Number(search.companyId)
          }
          return true
        })
        .map((o) => ({
          value: o.id,
          label: `${o.officeName} (${o.addressCity}, ${o.addressState})`,
        })),
    [offices, search.companyId],
  )
  const payersOptions = useMemo(
    () =>
      payers?.map((p) => ({
        value: p.payerId,
        label: p.name,
        matchedName: p.matchedName,
      })),
    [payers],
  )

  const bucketSoundAlertActive = !!tableHelpers.bucket && soundAlertBuckets.includes(tableHelpers.bucket)

  const toggleSoundAlert = async () => {
    if (!currentUserId || !tableHelpers.bucket) return

    const previousBuckets = soundAlertBuckets
    const next = bucketSoundAlertActive
      ? soundAlertBuckets.filter((b) => b !== tableHelpers.bucket)
      : [...soundAlertBuckets, tableHelpers.bucket]

    setSoundAlertBucketsState(next)
    dispatch(setNotificationBuckets(next))

    if (bucketSoundAlertActive) {
      soundNotificationService.unsubscribe(tableHelpers.bucket)
    } else {
      soundNotificationService.subscribe(tableHelpers.bucket)
    }

    try {
      await updateLabUserNotificationBuckets({
        id: currentUserId,
        notificationBuckets: next,
      }).unwrap()
    } catch (error: any) {
      setSoundAlertBucketsState(previousBuckets)
      dispatch(setNotificationBuckets(previousBuckets))
      if (bucketSoundAlertActive) {
        soundNotificationService.subscribe(tableHelpers.bucket)
      } else {
        soundNotificationService.unsubscribe(tableHelpers.bucket)
      }
      Toast({
        type: 'error',
        label: error?.data?.message || 'Failed to update notification settings.',
      })
    }
  }

  const savedBucketActive = currentSavedBucket
    ? savedBuckets.some(
        (savedBucket: SavedBucketDto) =>
          savedBucket.bucket === currentSavedBucket.bucket &&
          savedBucket.testType === currentSavedBucket.testType,
      )
    : false

  const toggleSavedBucket = async () => {
    if (!currentSavedBucket || !currentUserId) return

    const previousSavedBuckets = savedBuckets
    const nextSavedBuckets = savedBucketActive
      ? savedBuckets.filter(
          (savedBucket: SavedBucketDto) =>
            !(
              savedBucket.bucket === currentSavedBucket.bucket &&
              savedBucket.testType === currentSavedBucket.testType
            ),
        )
      : [...savedBuckets, currentSavedBucket]

    setLabSavedBucketsState(nextSavedBuckets)

    try {
      await updateLabUserSavedBuckets({
        id: currentUserId,
        savedBuckets: nextSavedBuckets,
      }).unwrap()
    } catch (error: any) {
      setLabSavedBucketsState(previousSavedBuckets)
      Toast({
        type: 'error',
        label: error?.data?.message || 'Failed to update saved buckets.',
      })
    }
  }

  const getOrderStateFilter = () => {
    if (search.verified) {
      return 'verified'
    }
    if (search.aobVerified) {
      return 'aobVerified'
    }
    if (search.hasUploads) {
      return 'hasUploads'
    }
    if (search.failedVerification) {
      return 'failedVerification'
    }
  }

  const exportTable = async () => {
    const escape = (str) => str
    const table = document.querySelector('table#data-table')
    if (!table) return
    const headers = [].slice.call(table.getElementsByTagName('thead')[0].getElementsByTagName('th'))
    var rows = [].slice.call(table.getElementsByTagName('tbody')[0].getElementsByTagName('tr'))

    const header = headers.map((header) => escape(header.innerText)).join(',')
    const data = rows
      .map((row) =>
        [].slice
          .call(row.getElementsByTagName('td'))
          .map((col) => escape(col.innerText))
          .join(','),
      )
      .join('\n')
    const csvData = [header, data].join('\n')
    const blob = new Blob([csvData], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'orders.csv'
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div className="p-2" style={{ width: '100%' }}>
      <FlexRow>
        <Button variant="link" onClick={clearSearch}>
          Clear
        </Button>
        {tableHelpers.showTestTypeFilter && (
          <ButtonGroup className="me-2" aria-label="First group">
            <Button
              active={tableHelpers.testType === 'POX_COX'}
              variant="secondary"
              onClick={() => tableHelpers.setTestType('POX_COX')}>
              POX/COX
            </Button>
            <Button
              active={tableHelpers.testType === 'HST'}
              variant="secondary"
              onClick={() => tableHelpers.setTestType('HST')}>
              HST
            </Button>
            <Button
              active={tableHelpers.testType === 'PSG'}
              variant="secondary"
              onClick={() => tableHelpers.setTestType('PSG')}>
              InLab
            </Button>
          </ButtonGroup>
        )}
        <div className="d-flex">
          {showExport && (
            <Button onClick={exportTable} variant="outline-primary" className="mr-3">
              <i className="fas fa-file-export mr-2"></i>
              Export
            </Button>
          )}
          <Dropdown
            initialValue={tableHelpers.pageSize}
            onClick={(v) => {
              tableHelpers.setCurrentPage(0)
              tableHelpers.setPageSize(Number(v))
            }}
            buttonTypeClass="btn-outline-primary"
            options={[
              { label: '12' },
              {
                label: '20',
              },
              {
                label: '50',
              },
              {
                label: '100',
              },
              {
                label: '200',
              },
            ]}
            additionalText="Records"
          />
          {!!currentSavedBucket && (
            <Button
              onClick={toggleSavedBucket}
              variant={savedBucketActive ? 'primary' : 'outline-primary'}
              className="ml-2"
              title={savedBucketActive ? 'Remove from saved buckets' : 'Save bucket'}
              css={{
                '& i': {
                  color: 'inherit !important',
                },
              }}>
              <i className={`${savedBucketActive ? 'fas' : 'far'} fa-star`} />
            </Button>
          )}
          {notificationBuckets.includes(tableHelpers?.bucket || '') && (
            <Button
              onClick={toggleSoundAlert}
              variant={bucketSoundAlertActive ? 'primary' : 'outline-primary'}
              className="ml-2"
              title={bucketSoundAlertActive ? 'Disable sound notifications' : 'Enable sound notifications'}
              css={{
                '& i': {
                  color: 'inherit !important',
                },
              }}>
              <i className={`${bucketSoundAlertActive ? 'fas' : 'far'} fa-bell`} />
            </Button>
          )}
        </div>
      </FlexRow>
      <div className="row mt-3" style={{ width: '100%' }}>
        <InputWrapper className="col-6 col-lg mb-3 mb-lg-0">
          <Input
            value={search.orderId}
            placeholder="Order ID"
            label="Order ID"
            name="orderId"
            handleChange={(e) => changeSearch('orderId', e.target.value)}
          />
        </InputWrapper>
        <div className="col-6 col-lg mb-3 mb-lg-0">
          <label className="text-gray" htmlFor="companyId">
            Company
          </label>
          <ReactSelect
            menuPortalTarget={document.body}
            styles={{ menuPortal: (base) => ({ ...base, zIndex: 10 }) }}
            className="basic-single w-100 mr-3"
            classNamePrefix="select"
            isClearable
            isSearchable
            placeholder="Type to search companies..."
            name="companyId"
            onChange={(item) => {
              changeSearch('companyId', item?.value)
              // Clear office selection when a company changes
              if (item?.value !== search.companyId) {
                changeSearch('officeId', '')
              }
              // Clear company search when selection is cleared
              if (!item) {
                onCompanySearch?.('')
              }
            }}
            onInputChange={(v) => v && onCompanySearch?.(v)}
            options={companiesOptions}
            value={companiesOptions?.find((c) => c.value === search.companyId) ?? null}
          />
        </div>
        <div className="col-6 col-lg mb-3 mb-lg-0">
          <label className="text-gray" htmlFor="officeId">
            DME Office
          </label>
          <ReactSelect
            menuPortalTarget={document.body}
            styles={{ menuPortal: (base) => ({ ...base, zIndex: 10 }) }}
            className="basic-single w-100 mr-3"
            classNamePrefix="select"
            isClearable
            isSearchable
            placeholder="Select DME Office"
            name="officeId"
            onChange={(item) => changeSearch('officeId', item?.value)}
            options={officesOptions}
            value={officesOptions?.find((o) => o.value == search.officeId) ?? null}
          />
        </div>
        <InputWrapper className="col-6 col-lg mb-3 mb-lg-0">
          <Input
            value={search.patientFirstName}
            placeholder="Patient First Name"
            label="Patient First Name"
            name="patientFirstName"
            handleChange={(e) => changeSearch('patientFirstName', e.target.value)}
          />
        </InputWrapper>
        <InputWrapper className="col-6 col-lg mb-3 mb-lg-0">
          <Input
            value={search.patientLastName}
            placeholder="Patient Last Name"
            label="Patient Last Name"
            name="patientLastName"
            handleChange={(e) => changeSearch('patientLastName', e.target.value)}
          />
        </InputWrapper>
        <InputWrapper className="col-6 col-lg mb-3 mb-lg-0">
          <InputMask
            value={search.patientDob}
            placeholder="Patient Date of Birth"
            label="Patient Date of Birth"
            type="text"
            name="patientDob"
            mask="99/99/9999"
            onChange={(event: ChangeEvent) => {
              changeSearch('patientDob', event.target.value)
            }}
          />
        </InputWrapper>
        {['INS_VER', 'BILLING_CHARGE_BLOCKED'].includes(tableHelpers.bucket) && (
          <div className="col-6 col-lg mb-3 mb-lg-0">
            <label className="text-gray" htmlFor="primaryPayorId">
              Primary Payor
            </label>
            <ReactSelect
              menuPortalTarget={document.body}
              styles={{ menuPortal: (base) => ({ ...base, zIndex: 10 }) }}
              className="basic-single w-100 mr-3"
              classNamePrefix="select"
              isClearable
              isSearchable
              placeholder="Select Primary Payor"
              name="primaryPayorId"
              onChange={(item) => {
                console.log(item)
                changeSearch('primaryPayorId', item?.value)
              }}
              options={payersOptions}
              formatOptionLabel={formatPayerOptionLabel}
              filterOption={filterPayerOption}
              value={payersOptions?.find((p) => p.value === search.primaryPayorId) ?? null}
              onInputChange={(v) => v && changeSearch('payerName', v)}
            />
          </div>
        )}
        <div className="col-6 col-lg mb-3 mb-lg-0" style={{ position: 'relative' }}>
          <label className="text-gray" htmlFor="physicianNpis">
            Physician
          </label>
          <ReactSelect
            menuPortalTarget={document.body}
            styles={{ menuPortal: (base) => ({ ...base, zIndex: 10 }) }}
            className="basic-single w-100 mr-3"
            classNamePrefix="select"
            isClearable
            isSearchable
            placeholder="Select Physician"
            name="physicianNpis"
            onChange={(item) => changeSearch('physicianNpis', item?.value)}
            options={physicianOptions}
            // A full name typed first-name-first still matches "Smith, Victoria".
            filterOption={(option, input) => matchesEveryWord(option.label, input)}
            value={physicianOptions?.find((p) => p.value === search.physicianNpis) ?? null}
            onInputChange={(v) => changeSearch('physicianName', v)}
            inputValue={search.physicianName}
          />
          {physiciansLoading && (
            <div style={{ position: 'absolute', top: 0, right: '10px' }}>
              <CircularProgress size={20} />
            </div>
          )}
        </div>
        {['ALL', 'ACTIVE'].includes(tableHelpers.bucket) && (
          <InputWrapper className="col-6 col-lg mb-3 mb-lg-0">
            <Select
              options={orderStateOptions}
              value={getOrderStateFilter()}
              defaultLabel="Select State Filter"
              label="Order State Filter"
              name="orderStateFilter"
              handleChange={(event: ChangeEvent) => {
                orderStateOptions.forEach((option) => {
                  console.log(
                    'change search',
                    event.target.value,
                    option.key === event.target.value,
                  )
                  changeSearch(option.key, option.key === event.target.value)
                })
              }}
            />
          </InputWrapper>
        )}
        {['AUTHORIZATION'].includes(tableHelpers.bucket) && (
          <InputWrapper className="col-6 col-lg mb-3 mb-lg-0">
            <Select
              options={authorizationStateOptions}
              value={search.authorizationState}
              defaultLabel="Select Filter"
              label="Order Filter"
              name="orderStateFilter"
              handleChange={(event: ChangeEvent) => {
                changeSearch('authorizationState', event.target.value)
              }}
            />
          </InputWrapper>
        )}
        {['COMPLETED'].includes(tableHelpers.bucket) && (
          <InputWrapper className="col-6 col-lg mb-3 mb-lg-0">
            <Select
              options={billingStateOptions}
              value={search.billingState}
              defaultLabel="Select Filter"
              label="Billing Filter"
              name="billingStateFilter"
              handleChange={(event: ChangeEvent) => {
                changeSearch('billingState', event.target.value)
              }}
            />
          </InputWrapper>
        )}
        {followUpFilterConfig?.showHumanFollowUpFilter && (
          <InputWrapper className="col-6 col-lg mb-3 mb-lg-0">
            <Select
              options={humanFollowUpOptions}
              value={search.humanFollowUpOnly ? 'humanOnly' : 'all'}
              defaultLabel="All Orders"
              label="Follow-up Filter"
              name="humanFollowUpFilter"
              handleChange={(event: ChangeEvent) => {
                changeSearch('humanFollowUpOnly', event.target.value === 'humanOnly')
              }}
            />
          </InputWrapper>
        )}
        {OUTSOURCE_VISIBILITY_BUCKETS.includes(tableHelpers.bucket ?? '') && (
          <InputWrapper className="col-6 col-lg mb-3 mb-lg-0">
            <Select
              options={outsourceVisibilityOptions}
              value={search.outsourceVisibility}
              defaultLabel="All Orders"
              label="Outsource Visibility"
              name="outsourceVisibilityFilter"
              handleChange={(event: ChangeEvent<HTMLSelectElement>) => {
                changeSearch('outsourceVisibility', event.target.value)
              }}
            />
          </InputWrapper>
        )}
        {hasWesperDropshipFilter(tableHelpers.testType, tableHelpers.bucket) && (
          <InputWrapper className="col-6 col-lg mb-3 mb-lg-0">
            <Select
              options={wesperDropshipOptions}
              value={search.showWesperDropship ? 'show' : 'hide'}
              defaultLabel="Hide Wesper Dropship"
              label="Wesper Dropship"
              name="wesperDropshipFilter"
              handleChange={(event: ChangeEvent<HTMLSelectElement>) => {
                changeSearch('showWesperDropship', event.target.value === 'show')
              }}
            />
          </InputWrapper>
        )}
      </div>
    </div>
  )
}
